import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, PageBody, EmptyState } from "../components/Shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";

const ACTION_COLORS = {
  login: "bg-slate-100 text-slate-700 ring-slate-700/10",
  logout: "bg-slate-100 text-slate-700 ring-slate-700/10",
  register: "bg-emerald-50 text-emerald-700 ring-emerald-700/10",
  create: "bg-emerald-50 text-emerald-700 ring-emerald-700/10",
  update: "bg-blue-50 text-blue-700 ring-blue-700/10",
  delete: "bg-red-50 text-red-700 ring-red-700/10",
  status_change: "bg-indigo-50 text-indigo-700 ring-indigo-700/10",
  bulk_status: "bg-indigo-50 text-indigo-700 ring-indigo-700/10",
  excel_upload: "bg-amber-50 text-amber-700 ring-amber-700/10",
  visit_add: "bg-emerald-50 text-emerald-700 ring-emerald-700/10",
  visit_delete: "bg-red-50 text-red-700 ring-red-700/10",
};

const ENTITY_TYPES = ["all", "user", "customer", "supplier", "item", "offer", "order"];
const ACTIONS = ["all", "login", "logout", "register", "create", "update", "delete", "status_change", "bulk_status", "excel_upload", "visit_add", "visit_delete"];

export default function Logs() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ user_id: "all", entity_type: "all", action: "all" });
  const [q, setQ] = useState("");

  const load = () => {
    const params = { limit: 500 };
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    return api.get("/audit-logs", { params }).then((r) => setRows(r.data));
  };

  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) =>
      r.entity_label?.toLowerCase().includes(s) ||
      r.user_email?.toLowerCase().includes(s) ||
      r.entity_id?.toLowerCase().includes(s) ||
      r.action?.toLowerCase().includes(s)
    );
  }, [rows, q]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const fmt = (ts) => {
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  return (
    <>
      <PageHeader title="Activity Logs" subtitle="Audit trail" testid="logs-title" />
      <PageBody>
        <div className="grid md:grid-cols-4 gap-3 mb-4 items-end">
          <div>
            <Label className="label-caps">Search</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Entity, user, action…" className="rounded-none mt-1" data-testid="logs-search" />
          </div>
          <div>
            <Label className="label-caps">User</Label>
            <Select value={filters.user_id} onValueChange={(v) => setF("user_id", v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="filter-user"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All users</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Entity</Label>
            <Select value={filters.entity_type} onValueChange={(v) => setF("entity_type", v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="filter-entity"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                {ENTITY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Action</Label>
            <Select value={filters.action} onValueChange={(v) => setF("action", v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="filter-action"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {filtered.length === 0 ? <EmptyState title="No activity yet" hint="Actions taken by any user will appear here." /> :
          <div className="bg-white border border-slate-300 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-300">
                <tr className="text-left">
                  <th className="py-2 px-3 label-caps">Time</th>
                  <th className="py-2 px-3 label-caps">User</th>
                  <th className="py-2 px-3 label-caps">Action</th>
                  <th className="py-2 px-3 label-caps">Entity</th>
                  <th className="py-2 px-3 label-caps">Target</th>
                  <th className="py-2 px-3 label-caps">Details</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-slate-200 hover:bg-slate-50 stagger-row" data-testid={`log-row-${r.id}`}>
                    <td className="py-2 px-3 font-mono text-slate-600 whitespace-nowrap">{fmt(r.timestamp)}</td>
                    <td className="py-2 px-3">
                      <div className="font-medium">{r.user_name || "-"}</div>
                      <div className="text-xs font-mono text-slate-500">{r.user_email || "-"}</div>
                    </td>
                    <td className="py-2 px-3">
                      <span className={`text-xs font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm ring-1 ring-inset ${ACTION_COLORS[r.action] || "bg-slate-100 text-slate-700 ring-slate-700/10"}`}>{r.action}</span>
                    </td>
                    <td className="py-2 px-3">
                      <span className="text-xs font-mono uppercase tracking-wider text-slate-500">{r.entity_type}</span>
                    </td>
                    <td className="py-2 px-3 font-mono">{r.entity_label || <span className="text-slate-400">—</span>}</td>
                    <td className="py-2 px-3 text-slate-600 text-xs max-w-md truncate">
                      {r.details && Object.keys(r.details).length > 0 ? JSON.stringify(r.details) : <span className="text-slate-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </PageBody>
    </>
  );
}
